/**
 * Shared model for turning a seat reply's raw markdown text into something
 * both a chat bubble and a speech engine can consume safely. Angular-free so
 * both platforms (NativeScript + web) and the tests consume it directly.
 */

/** A markdown table, already split into header + body cells. */
export interface ChatMarkdownTable {
  readonly headers: readonly string[];
  readonly rows: readonly (readonly string[])[];
}

/** A standalone markdown image (`![alt](url)` on its own line). */
export interface ChatMarkdownImage {
  readonly url: string;
  readonly alt: string;
  /**
   * Bug 34653: only an https URL may be loaded. A non-https image URL still
   * comes back as an 'image' segment (so the caller never has to re-parse
   * the message) but with `allowed: false`, so the renderer shows the alt
   * text without ever attempting the load.
   */
  readonly allowed: boolean;
}

/**
 * A render-ready slice of a message's content. `markdown` segments are
 * passed through verbatim to a native markdown renderer (bold/italic/links/
 * lists render fine there); `table` segments carry pre-parsed cells because
 * the native renderers in this app (Markwon / TSMarkdownParser) render a
 * pipe table as literal text rather than a grid; `image` segments carry a
 * standalone markdown image's url/alt for inline rendering (bug 34653).
 */
export type ChatMarkdownSegment =
  | { readonly kind: 'markdown'; readonly text: string }
  | ({ readonly kind: 'table' } & ChatMarkdownTable)
  | ({ readonly kind: 'image' } & ChatMarkdownImage);
